import moment from 'moment';
import get from 'lodash/get';
import merge from 'lodash/merge';
import invert from 'lodash/invert';
import isDate from 'lodash/isDate';
import isUndefined from 'lodash/isUndefined';
import mapKeys from 'lodash/mapKeys';
import pick from 'lodash/pick';
import mapValues from 'lodash/mapValues';
import pickBy from 'lodash/pickBy';
import omitBy from 'lodash/omitBy';
import omit from 'lodash/omit';

// Page size requested from tsf-search. Exported so the saga, the paginator and the
// MAX_PAGE ceiling below all derive from a single value instead of repeating `40`.
export const RESULTS_PER_PAGE = 40;

// Elasticsearch caps deep pagination at index.max_result_window (default 10000):
// tsf-search builds `from: (page - 1) * per_page` (lib/queries/pagination.ex), and
// from + size beyond the window makes the query fail, so the last reachable page is
// MAX_RESULT_WINDOW / RESULTS_PER_PAGE.
export const MAX_RESULT_WINDOW = 10000;
export const MAX_PAGE = Math.floor(MAX_RESULT_WINDOW / RESULTS_PER_PAGE);

const URL_PARAMS = [
  'address', 'zoom', 'space_type_ids',
  'listing_feature_ids', 'listing_feature_ids_contains_array',
  'project_type_ids', 'parent_project_type_id', 'position_types',
  'latitude_gteq', 'latitude_lteq', 'longitude_gteq', 'longitude_lteq',
  'latitude', 'longitude', 'daily_price_gteq', 'daily_price_lteq',
  'weekly_price_gteq', 'weekly_price_lteq',
  'monthly_price_gteq', 'monthly_price_lteq',
  'lat_g', 'lat_l', 'lng_g', 'lng_l', 'parent_project_type_ids_contains_element',
  'size_gteq', 'size_lteq', 's', 'country', 'city',
  'duration', 'start_date', 'end_date', 'fit_bounds', 'foot_traffic',
  'is_for_lease', 'is_for_revenue_share', 'is_for_sale', 'is_for_short_term',
];

const LOCATION_TO_PARAMS = {
  lat_g: 'latitude_gteq',
  lat_l: 'latitude_lteq',
  lng_g: 'longitude_gteq',
  lng_l: 'longitude_lteq',
  listing_feature_ids: 'listing_feature_ids_contains_array',
};

const PARAMS_TO_LOCATION = invert(LOCATION_TO_PARAMS);

function mapParams(params, mapping) {
  const mapped = mapKeys(params, (_value, param) => get(mapping, param, param));
  return pick(mapped, URL_PARAMS);
}

function paramsToLocation(params) {
  return mapParams(params, PARAMS_TO_LOCATION);
}

function locationToParams(location) {
  return mapParams(location, LOCATION_TO_PARAMS);
}

function datesToParams(params) {
  return mapValues(params, (value) => (
      isDate(value)
        ? moment.utc(value).local().format('YYYY-MM-DD')
        : value
    ));
}

function update(oldParams, newParams) {
  const merged = merge({}, oldParams, newParams);
  const withoutUndefined = pickBy(merged, (value) => !isUndefined(value));
  const withoutNull = omitBy(withoutUndefined, (value) => value === null);
  return omit(withoutNull, '_');
}

export const toUrlParams = function (filters, page) {
  return merge(paramsToLocation(filters), { page });
};

// Returns the raw query string of a URL, i.e. everything after the first "?".
// Handles every /search route shape (/search, /search/:market, /search/:market/:projectType)
// and every locale prefix (/fr, /it, /nl, /hk, /kr), and returns '' when there is no
// query string so callers parse an empty object instead of the path itself.
export const toQueryString = function (url) {
  const href = url == null ? '' : String(url);
  const queryStart = href.indexOf('?');

  return queryStart === -1 ? '' : href.slice(queryStart + 1);
};

// Coerces an untrusted `page` (query string values are arbitrary) into an integer
// within [1, MAX_PAGE]. Anything ambiguous or out of range falls back to the first page:
// tsf-search neither validates nor clamps this value, so an unusable page reaches
// Elasticsearch and fails the whole search request.
//
// Note that `page` usually arrives already converted, because the query string is parsed
// with `parseNumbers`. That conversion is what turns "0x10" into 16, so a non-decimal
// literal is a valid page by the time it gets here. Rejecting it would mean changing how
// every search parameter is parsed, and the value is in range and harmless either way.
export const toPageNumber = function (value) {
  // A repeated `page=3&page=4` parses to an array: the intent is ambiguous, so
  // rather than guessing an element we fall back to the first page.
  if (Array.isArray(value)) {
    return 1;
  }

  if (typeof value !== 'number' && typeof value !== 'string') {
    return 1;
  }

  const parsed = typeof value === 'number' ? value : Number(value.trim());

  // Rejects NaN and ±Infinity, which have no sensible page to fall back from.
  if (!Number.isFinite(parsed)) {
    return 1;
  }

  // Truncates fractional pages ("3.7" -> 3) rather than rejecting them, so a rounded
  // or mangled link still lands on a plausible page instead of resetting to the first.
  const page = Math.trunc(parsed);

  // Out of range in either direction falls back to the first page rather than clamping to
  // MAX_PAGE. No real search comes close to the ceiling (795 results is 20 pages), so a
  // page above it is a bad link, and pinning it to page 250 would answer with an empty
  // result list and HTTP 200 instead of the results the user can actually use.
  if (page < 1 || page > MAX_PAGE) {
    return 1;
  }

  return page;
};

export const toFilters = function (oldParams, newParams) {
  return update(oldParams, locationToParams(datesToParams(newParams)));
};
